import { Action, Dispatch } from 'redux';
import { Properties, TimeSlot, Visit } from './types';
import { ReduxAction, failure, pending, success } from '../core';

import { HealAPI } from '../../healApi';
import { ThunkAction } from 'redux-thunk';
import { toast } from '../message/actions';

export class VisitsAction {
  public static FETCH_VISITS = `VISITS_FETCH_VISITS`;
  public static SET_IS_FETCHING = `VISITS_SET_IS_FETCHING`;
  public static SET_VISITS = `VISITS_SET_VISITS`;
  public static SET_VISIT = `VISITS_SET_VISIT`;
  public static FETCH_VISIT = `VISITS_FETCH_VISIT`;

  public static SET_VISIT_CONFIG_FETCHING = `VISITS_SET_VISIT_CONFIG_FETCHING`;
  public static SET_VISIT_CONFIG = `VISITS_SET_VISIT_CONFIG`;
  public static SET_AVAILABLE_TIME_SLOTS = `VISITS_SET_AVAILABLE_TIME_SLOTS`;
  public static SET_AVAILABLE_TIME_SLOTS_FETCHING = `VISITS_SET_AVAILABLE_TIME_SLOTS_FETCHING`;

  public static END_VISIT = `VISITS_END_VISIT`;
  public static START_VISIT = `VISITS_START_VISIT`;
  public static REFRESH_VISITS = `VISITS_REFRESH_VISITS`;
  public static REFUND_VISIT = `VISITS_REFUND_VISIT`;
  public static ADD_INSURANCE = `VISITS_ADD_INSURANCE`;
  public static CHANGE_PROVIDER = `VISITS_CHANGE_PROVIDER`;
  public static UPDATE_TIME = `VISITS_UPDATE_TIME`;

  public static SET_CANCEL_REASONS = `VISITS_SET_CANCEL_REASONS`;
  public static SET_CANCEL_REASONS_FETCHING = `VISITS_SET_CANCEL_REASONS_FETCHING`;
}

export function setVisitsFetching(value: boolean): ReduxAction<boolean> {
  return {
    type: VisitsAction.SET_IS_FETCHING,
    data: value,
  };
}

export function setVisits(value: Visit[]): ReduxAction<Visit[]> {
  return {
    type: VisitsAction.SET_VISITS,
    data: value,
  };
}

export function setVisit(value: Visit): ReduxAction<Visit> {
  return {
    type: VisitsAction.SET_VISIT,
    data: value,
  };
}

export function fetchVisit(visitCode: string, refresh?: boolean): ReduxAction<{ visitCode: string; refresh?: boolean }> {
  return {
    type: VisitsAction.FETCH_VISIT,
    data: {
      visitCode,
      refresh,
    },
  };
}

export function setVisitConfigFetching(value: boolean): ReduxAction<boolean> {
  return {
    type: VisitsAction.SET_VISIT_CONFIG_FETCHING,
    data: value,
  };
}

export function setVisitConfig(value: Properties): ReduxAction<Properties> {
  return {
    type: VisitsAction.SET_VISIT_CONFIG,
    data: value,
  };
}

export function startVisit(visitCode: string, date: Date): ThunkAction<any, any, any, any> {
  return (dispatch: Dispatch<Action>) => {
    dispatch({ type: pending(VisitsAction.START_VISIT) });
    return HealAPI.startVisit(visitCode, date)
      .then((response) => {
        if (response.ok) {
          dispatch(
            toast({
              type: 'success',
              title: 'Success',
              content: 'Successfully started ' + visitCode,
            })
          );
          dispatch({ type: success(VisitsAction.START_VISIT), data: response.data });
        } else {
          dispatch(toast({ type: 'error', title: 'Error', content: 'Could not start visit.' }));
          dispatch({ type: failure(VisitsAction.START_VISIT), data: response.data });
        }
      })
      .catch((error) => {
        dispatch(
          toast({
            type: 'error',
            title: 'Error',
            content: error.response.body.description || 'Could not start visit.',
          })
        );
        dispatch({ type: failure(VisitsAction.START_VISIT), data: error });
      });
  };
}

export function endVisit(visitCode: string, date: Date): ThunkAction<any, any, any, any> {
  return (dispatch: Dispatch<Action>) => {
    dispatch({ type: pending(VisitsAction.END_VISIT) });
    return HealAPI.endVisit(visitCode, date)
      .then((response) => {
        if (response.ok) {
          dispatch(toast({ type: 'success', title: 'Success', content: 'Successfully ended ' + visitCode }));
          dispatch({ type: success(VisitsAction.END_VISIT), data: response.data });
        } else {
          dispatch(toast({ type: 'error', title: 'Error', content: 'Could not end visit.' }));
          dispatch({ type: failure(VisitsAction.END_VISIT), data: response.data });
        }
      })
      .catch((error) => {
        dispatch(
          toast({
            type: 'error',
            title: 'Error',
            content: error.response.body.description || 'Could not end visit.',
          })
        );
        dispatch({ type: failure(VisitsAction.END_VISIT), data: error });
      });
  };
}

export function addInsurance(visitCode: string): ReduxAction<string> {
  return {
    type: VisitsAction.ADD_INSURANCE,
    data: visitCode,
  };
}

export function changeProvider(
  visitCode: string,
  overridePCPProvider: boolean,
  patientAffected?: boolean,
  doctorId?: string,
  assistantId?: string,
  timeSlotId?: string,
  time?: number
): ThunkAction<any, any, any, any> {
  return (dispatch: Dispatch<Action>) => {
    dispatch({ type: pending(VisitsAction.CHANGE_PROVIDER) });
    return HealAPI.changeProvider(visitCode, overridePCPProvider, patientAffected, doctorId, assistantId, timeSlotId, time)
      .then((response) => {
        if (response.ok) {
          dispatch({ type: success(VisitsAction.CHANGE_PROVIDER), data: response.data });
        } else {
          dispatch({ type: failure(VisitsAction.CHANGE_PROVIDER), data: response.data });
        }
      })
      .catch((error) => {
        dispatch({ type: failure(VisitsAction.CHANGE_PROVIDER), data: error });
      });
  };
}

export function setAvailableTimeSlotsFetching(value: boolean): ReduxAction<boolean> {
  return {
    type: VisitsAction.SET_AVAILABLE_TIME_SLOTS_FETCHING,
    data: value,
  };
}

export function setAvailableTimeSlots(visitCode: string, value: TimeSlot[]): ReduxAction<{ visitCode: string; value: TimeSlot[] }> {
  return {
    type: VisitsAction.SET_AVAILABLE_TIME_SLOTS,
    data: {
      visitCode,
      value,
    },
  };
}

export function fetchTimeslots(visitCode: string) {
  return (dispatch: Dispatch) => {
    dispatch(setAvailableTimeSlotsFetching(true));
    return HealAPI.fetchAvailableTimeSlots(visitCode).then((response) => {
      if (response.ok && response.data) {
        dispatch(setAvailableTimeSlots(visitCode, response.data));
      }
      dispatch(setAvailableTimeSlotsFetching(false));
    });
  };
}

export function fetchCancelReasons() {
  return async (dispatch: Dispatch) => {
    dispatch({ type: VisitsAction.SET_CANCEL_REASONS_FETCHING });
    const response = await HealAPI.fetchCancelReasons();
    if (response.ok && response.data) {
      dispatch({ type: VisitsAction.SET_CANCEL_REASONS, data: response.data.data });
    }
  };
}
